import { Localized } from "@fluent/react"
import { Alert, AlertTitle, Button, Typography } from "@mui/material"
import { Component, ErrorInfo, ReactNode } from "react"

interface ErrorBoundaryProps {
    children: ReactNode
    /** Rendered in place of the subtree when it fails; defaults to the alert below. */
    fallback?: ReactNode
    /**
     * The boundary latches its error until one of these changes. Pass whatever
     * identifies the content being rendered (a document id, say) so moving to
     * different content clears a failure that belonged to the previous one.
     */
    resetKeys?: unknown[]
    onError?: (error: Error, info: ErrorInfo) => void
}

interface ErrorBoundaryState {
    error: Error | null
}

/**
 * Catches render-time errors in its subtree.
 *
 * React unmounts the *entire* tree when a render throws and nothing catches it,
 * so without a boundary one bad node blanks the whole application. Wrap the
 * parts that render data we don't control — imported supplier content above all
 * — close enough to the risk that a failure costs a panel rather than the page.
 *
 * Only render, lifecycle and constructor errors reach here: event handlers and
 * async callbacks still need their own try/catch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error }
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("Render failed below an ErrorBoundary:", error, info.componentStack)
        this.props.onError?.(error, info)
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        const { resetKeys } = this.props
        if (this.state.error === null || resetKeys === undefined) return

        const previous = prevProps.resetKeys
        const changed = previous === undefined
            || previous.length !== resetKeys.length
            || resetKeys.some((key, i) => !Object.is(key, previous[i]))

        if (changed) this.setState({ error: null })
    }

    handleRetry = () => this.setState({ error: null })

    render() {
        const { error } = this.state
        if (error === null) return this.props.children
        if (this.props.fallback !== undefined) return this.props.fallback

        return <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
                <Button color="inherit" size="small" onClick={this.handleRetry}>
                    <Localized id="try-again">Try again</Localized>
                </Button>
            }
        >
            <AlertTitle>
                <Localized id="error-render-failed-title">This part of the page could not be displayed</Localized>
            </AlertTitle>
            <Localized id="error-render-failed">
                <span>The rest of the page still works. Try again, and report it to us if it keeps happening.</span>
            </Localized>
            {/* Kept visible on purpose: it is the first thing support asks for. */}
            <Typography variant="caption" component="div" sx={{ mt: 1, fontFamily: "monospace", opacity: 0.75 }}>
                {error.message}
            </Typography>
        </Alert>
    }
}
